/**
 * Minimal typed loader for the YouTube IFrame Player API (Phase Q.1 §2.1).
 *
 * The API script (https://www.youtube.com/iframe_api) is injected once and
 * signals readiness by calling the global `window.onYouTubeIframeAPIReady`. We
 * wrap that one-shot callback in a shared promise so every YouTubeLivePlayer
 * instance awaits the same load, and chain any pre-existing callback so we don't
 * clobber another consumer.
 */

export interface YTPlayer {
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  getPlayerState(): number;
  /** Seconds elapsed in the current video. For a live stream it advances while
   *  real frames are decoding, and freezes when playback stalls — the one
   *  pixel-free signal that lets us tell a genuinely-playing live feed from a
   *  player that claims PLAYING over a stalled/black frame (cross-origin iframe,
   *  so videoWidth/pixels are unreadable). */
  getCurrentTime(): number;
  destroy(): void;
}

export interface YTPlayerEvent {
  target: YTPlayer;
  data: number;
}

export interface YTPlayerOptions {
  videoId: string;
  playerVars?: Record<string, number | string>;
  events?: {
    onReady?: (e: YTPlayerEvent) => void;
    onStateChange?: (e: YTPlayerEvent) => void;
    onError?: (e: YTPlayerEvent) => void;
  };
}

export interface YTNamespace {
  Player: new (el: HTMLElement | string, opts: YTPlayerOptions) => YTPlayer;
  PlayerState: {
    UNSTARTED: number;
    ENDED: number;
    PLAYING: number;
    PAUSED: number;
    BUFFERING: number;
    CUED: number;
  };
}

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

let apiPromise: Promise<YTNamespace> | null = null;

/** Resolve with the loaded YT namespace, injecting the API script on first use. */
export function loadYouTubeIframeApi(): Promise<YTNamespace> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('YouTube IFrame API requires a browser'));
  }
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise<YTNamespace>((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = (): void => {
      prev?.();
      if (window.YT?.Player) resolve(window.YT);
    };
    // If another module already added the tag, don't add a second.
    if (!document.querySelector(`script[src="${IFRAME_API_SRC}"]`)) {
      const tag = document.createElement('script');
      tag.src = IFRAME_API_SRC;
      document.head.appendChild(tag);
    }
  });
  return apiPromise;
}

/** Test seam — reset the shared load promise. */
export function __resetYoutubeIframeApi(): void {
  apiPromise = null;
}
